import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { getMyVenues } from '../services/venueService';

const empty = { firstName: '', lastName: '', email: '', password: '', venueIds: [] };
export default function VenueManagerManagement() {
  const [managers, setManagers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [existingPlayer, setExistingPlayer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [searchState, setSearchState] = useState('idle');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  useEffect(() => {
    const query = form.email.trim();
    let active = true;
    setSuggestions([]); setActiveSuggestion(-1); setSearchState('idle');
    if (query.length < 2 || selectedPlayer?.email === query) return;
    const timer = window.setTimeout(async () => {
      setSearchState('loading');
      try {
        const { data } = await api.get('/users/search', { params: { q: query, includeOwnBookingStaff: true } });
        if (active) { setSuggestions(data.users || []); setSearchState('done'); }
      } catch {
        if (active) setSearchState('error');
      }
    }, 300);
    return () => { active = false; window.clearTimeout(timer); };
  }, [form.email, selectedPlayer]);
  function selectPlayer(player) {
    setForm(current => ({ ...current, email: player.email, firstName: '', lastName: '', password: '' }));
    setSelectedPlayer(player); setSuggestions([]); setSuggestionsOpen(false); setActiveSuggestion(-1);
  }
  function navigateSuggestions(event) {
    if (event.key === 'Escape') { setSuggestionsOpen(false); return; }
    if (event.key === 'ArrowDown' && suggestions.length) {
      event.preventDefault(); setSuggestionsOpen(true); setActiveSuggestion(index => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp' && suggestions.length) {
      event.preventDefault(); setActiveSuggestion(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && suggestionsOpen && activeSuggestion >= 0) {
      event.preventDefault(); selectPlayer(suggestions[activeSuggestion]);
    }
  }
  useEffect(() => {
    let active = true;
    Promise.all([api.get('/venue-managers'), getMyVenues()]).then(([response, list]) => {
      if (active) { setManagers(response.data); setVenues(list); }
    }).catch(() => { if (active) setError('Could not load venue managers. Refresh to try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  function toggleVenue(id) {
    setForm((current) => ({ ...current, venueIds: current.venueIds.includes(id)
      ? current.venueIds.filter((v) => v !== id) : [...current.venueIds, id] }));
  }
  async function save(event, confirmExisting = false) {
    event?.preventDefault();
    if (!form.venueIds.length) { toast.error('Select at least one venue'); return; }
    setSaving(true);
    let successMessage = 'Venue manager access saved';
    try {
      if (editing) {
        const { data } = await api.put('/venue-managers/' + editing.id + '/access', { venueIds: form.venueIds, isActive: editing.isActive });
        setManagers((list) => list.map((m) => m.id === editing.id ? { ...m, ...data } : m));
      } else {
        const { data } = await api.post('/venue-managers', confirmExisting ? { ...existingPlayer.payload, confirmExistingPlayerAccount: true } : form);
        successMessage = data.message || successMessage;
        setManagers((list) => list.some((m) => m.id === data.id) ? list.map((m) => m.id === data.id ? { ...m, ...data } : m) : [...list, data]);
      }
      setForm(empty); setEditing(null); setExistingPlayer(null); setSelectedPlayer(null); toast.success(successMessage);
    } catch (err) {
      if (err.response?.data?.code === 'EXISTING_PLAYER_CONFIRMATION_REQUIRED') {
        setExistingPlayer({ ...err.response.data.player, payload: { ...form, venueIds: [...form.venueIds] } });
      } else toast.error(err.response?.data?.message || 'Could not save venue manager');
    }
    finally { setSaving(false); }
  }
  async function toggle(manager) {
    setSaving(true);
    try {
      const { data } = await api.put('/venue-managers/' + manager.id + '/access', { venueIds: manager.venueIds, isActive: !manager.isActive });
      setManagers((list) => list.map((m) => m.id === manager.id ? { ...m, ...data } : m));
      toast.success(data.isActive ? 'Manager access restored' : 'Manager access suspended');
    } catch (err) { toast.error(err.response?.data?.message || 'Could not update access'); }
    finally { setSaving(false); }
  }
  return <section className="card p-6 space-y-5">
    <div><h2 className="text-lg font-bold text-[#163E4A]">Venue managers</h2>
      <p className="mt-1 text-sm text-[#64748B]">Give a FREAC player manager access to selected venues. They keep the same email, password, profile and player bookings.</p>
      <p className="mt-2 text-xs text-[#64748B]">Bank details, payouts, ownership, venue deletion, marketing campaigns and user access grants remain owner-only.</p></div>
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    {loading ? <p>Loading managers…</p> : !error && <>
      <form onSubmit={save} className="rounded-xl bg-[#F8FAFC] p-4 space-y-4">
        <h3 className="font-semibold">{editing ? 'Assigned venues for ' + editing.firstName : 'Add venue manager'}</h3>
        {!editing && <>
          <div className="grid gap-3 md:grid-cols-2">
            {['firstName', 'lastName', 'email', 'password'].map((key) => key === 'email' ? <div key={key} className="relative">
              <label htmlFor="venue-manager-email" className="block text-sm">FREAC account email</label>
              <input id="venue-manager-email" className="input-field mt-1" type="email" required maxLength={254} autoComplete="off" placeholder="Search by name or email"
                role="combobox" aria-autocomplete="list" aria-expanded={suggestionsOpen && suggestions.length > 0}
                aria-controls="venue-manager-suggestions" aria-activedescendant={suggestionsOpen && activeSuggestion >= 0 ? 'venue-manager-option-' + activeSuggestion : undefined}
                value={form.email} onFocus={() => setSuggestionsOpen(true)} onBlur={() => setSuggestionsOpen(false)} onKeyDown={navigateSuggestions}
                onChange={(e) => { setSelectedPlayer(null); setSuggestionsOpen(true); update('email', e.target.value); }} />
              {selectedPlayer && <p className="mt-2 text-sm text-teal-700">Selected: {selectedPlayer.firstName} {selectedPlayer.lastName} · {selectedPlayer.isBookingStaff ? 'Existing booking-staff account' : 'Existing FREAC player'}</p>}
              {suggestionsOpen && form.email.trim().length >= 2 && !selectedPlayer && searchState !== 'idle' && <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                {searchState === 'loading' && <p className="p-3 text-sm text-slate-500" role="status">Searching FREAC accounts…</p>}
                {searchState === 'error' && <p className="p-3 text-sm text-slate-500" role="status">Search unavailable. You can still enter the full account email.</p>}
                {searchState === 'done' && !suggestions.length && <p className="p-3 text-sm text-slate-500" role="status">No matching player. Enter their full email and new-player details if they are new to FREAC.</p>}
                <ul id="venue-manager-suggestions" role="listbox" aria-label="FREAC player suggestions">
                  {suggestions.map((player, index) => <li key={player.id} role="presentation"><button id={'venue-manager-option-' + index} type="button" role="option" aria-selected={index === activeSuggestion}
                    className={'block w-full px-4 py-3 text-left text-sm hover:bg-teal-50 ' + (index === activeSuggestion ? 'bg-teal-50' : '')}
                    onMouseDown={(e) => e.preventDefault()} onClick={() => selectPlayer(player)}>
                    <span className="block font-medium text-slate-800">{player.firstName} {player.lastName}</span><span className="text-slate-500">{player.email}</span>
                  </button></li>)}
                </ul>
              </div>}
            </div> : <label key={key} className="text-sm">
              {{ firstName: 'First name (new players only)', lastName: 'Last name (new players only)', password: 'Password (new players only)' }[key]}
              <input className="input-field mt-1" type={key === 'password' ? 'password' : 'text'}
                minLength={key === 'password' ? 8 : 1} maxLength={key === 'password' ? 128 : 100}
                autoComplete={key === 'password' ? 'new-password' : 'off'} value={form[key]} onChange={(e) => update(key, e.target.value)} />
            </label>)}
          </div>
          <p className="text-xs text-[#64748B]">For an existing player, enter only their email. Enter names and a temporary password only when creating a new player. New players receive the usual verification email before they can sign in.</p>
        </>}
        <fieldset><legend className="mb-2 text-sm font-semibold">Assigned venues</legend>
          <div className="grid gap-2 sm:grid-cols-2">{venues.map((v) => <label key={v.id} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.venueIds.includes(v.id)} onChange={() => toggleVenue(v.id)} />{v.name}
          </label>)}</div>
        </fieldset>
        <div className="flex gap-2"><button disabled={saving || !venues.length} className="btn-primary">{saving ? 'Saving…' : editing ? 'Save assigned venues' : 'Add venue manager'}</button>
          {editing && <button type="button" className="btn-outline" onClick={() => { setEditing(null); setForm(empty); }}>Cancel</button>}</div>
      </form>
      <div className="divide-y">{managers.length === 0 ? <p className="text-sm text-[#64748B]">No venue managers yet.</p> : managers.map((m) => <div key={m.id} className="flex flex-wrap justify-between gap-3 py-4">
        <div><p className="font-semibold">{m.firstName} {m.lastName} <span className={'ml-2 text-xs ' + (m.isActive ? 'text-emerald-700' : 'text-red-600')}>{m.isActive ? 'Active' : 'Suspended'}</span></p>
          <p className="text-sm text-[#64748B]">{m.email}</p><p className="mt-1 text-xs text-[#64748B]">{m.venueIds.map((id) => venues.find((v) => v.id === id)?.name || 'Unavailable venue').join(', ') || 'No assigned venues'}</p></div>
        <div className="flex flex-wrap gap-2"><button type="button" disabled={saving} className="btn-outline" onClick={() => {
          setEditing(m); setSelectedPlayer(null); setSuggestionsOpen(false); setForm({ ...empty, venueIds: [...m.venueIds] });
        }}>Edit venues</button><button type="button" disabled={saving} className="btn-outline" onClick={() => toggle(m)}>{m.isActive ? 'Suspend access' : 'Restore access'}</button></div>
      </div>)}</div>
    </>}
    {existingPlayer && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="manager-confirm-title" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h3 id="manager-confirm-title" className="text-lg font-bold text-[#163E4A]">Grant venue manager access?</h3>
        <p className="mt-3 font-semibold">{existingPlayer.firstName} {existingPlayer.lastName}</p>
        <p className="text-sm text-[#64748B]">{existingPlayer.email}</p>
        <p className="mt-4 rounded-xl bg-teal-50 p-4 text-sm text-teal-900">{existingPlayer.canConvertBookingStaff ? 'This booking-staff account will become a player account with manager access, using the same email and password. Its existing staff access stays suspended if already suspended.' : 'This player will use their existing FREAC email and password. Their profile and bookings stay unchanged. Suspending manager access does not suspend their player account.'}</p>
        <div className="mt-5 flex justify-end gap-3">
          <button type="button" disabled={saving} className="btn-outline" onClick={() => setExistingPlayer(null)}>Cancel</button>
          <button type="button" disabled={saving} className="btn-primary" onClick={() => save(null, true)}>{saving ? 'Granting…' : 'Confirm manager access'}</button>
        </div>
      </div>
    </div>}
  </section>;
}
